import type { ReactNode, HTMLAttributes } from 'react';
import styles from './NavItem.module.css';

type TrackingValue = string | number | boolean;

export interface NavItemProps extends Omit<HTMLAttributes<HTMLElement>, 'children'> {
  icon: ReactNode;
  label: string;
  href?: string;
  isActive?: boolean;
  variant?: 'default' | 'danger';
  target?: string;
  rel?: string;
  trackEvent?: string;
  trackData?: Record<string, TrackingValue | null | undefined>;
}

export function NavItem({
  icon,
  label,
  href,
  isActive = false,
  variant = 'default',
  onClick,
  trackEvent,
  trackData,
  className,
  ...props
}: NavItemProps) {
  const classes = [
    styles.navItem,
    isActive && styles.active,
    variant === 'danger' && styles.danger,
    className,
  ]
    .filter(Boolean)
    .join(' ');

  const Comp = href ? 'a' : 'button';

  const trackingAttributes: Record<string, string> = {};
  if (trackEvent) {
    trackingAttributes['data-track'] = trackEvent;

    if (trackData) {
      for (const [key, value] of Object.entries(trackData)) {
        if (value == null) continue;
        const attrKey = `data-${key.replace(/[A-Z]/g, m => `-${m.toLowerCase()}`)}`;
        trackingAttributes[attrKey] = String(value);
      }
    }
  }

  return (
    <Comp
      className={classes}
      href={href}
      onClick={onClick}
      aria-current={isActive ? 'page' : undefined}
      {...(Comp === 'button' ? { type: 'button' } : {})}
      {...trackingAttributes}
      {...props}
    >
      {isActive && <span className={styles.activeIndicator} aria-hidden="true" />}
      <span className={styles.icon}>{icon}</span>
      <span className={styles.label}>{label}</span>
    </Comp>
  );
}

NavItem.displayName = 'NavItem';
